import { useCallback, useEffect, useState } from 'react';

const KEY_PREFIX = 'compose.panel.lastActed.';
const keyFor = (personaId?: string): string => `${KEY_PREFIX}${personaId || 'default'}`;

const read = (personaId?: string): string | null => {
  try {
    return localStorage.getItem(keyFor(personaId));
  } catch {
    return null;
  }
};

const write = (personaId: string | undefined, tabId: string): void => {
  try {
    localStorage.setItem(keyFor(personaId), tabId);
  } catch {
    /* storage unavailable */
  }
};

export interface UseLastActedTab {
  lastActedTabId: string | null;
  recordTab: (tabId: string) => void;
}

/**
 * Per-persona last-acted tab id. Powers the single ✱ badge on JourneyTabs.
 * recordTab updates state + persists to localStorage. Re-reads on
 * personaId change so a persona switch shows the new persona's history.
 * Subscribes to storage events so a second browser tab acting on the same
 * persona keeps both tabs' ✱ badges in sync.
 */
export function useLastActedTab(personaId?: string): UseLastActedTab {
  const [lastActedTabId, setLastActedTabId] = useState<string | null>(() => read(personaId));

  useEffect(() => {
    setLastActedTabId(read(personaId));
  }, [personaId]);

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === keyFor(personaId)) {
        setLastActedTabId(read(personaId));
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [personaId]);

  const recordTab = useCallback(
    (tabId: string): void => {
      setLastActedTabId(tabId);
      write(personaId, tabId);
    },
    [personaId],
  );

  return { lastActedTabId, recordTab };
}
